/**
 * Appearance + accessibility helpers shared by App.vue (topbar toggle) and the
 * Account > Preferences view. All settings are applied to <html> so they affect
 * the whole SPA, and mirrored to localStorage so they survive a reload before
 * the API round-trip finishes.
 */

export const THEMES = ['default', 'dark', 'high_contrast', 'athena'];
export const FONT_SIZES = ['small', 'medium', 'large', 'x-large'];

const KEYS = {
  theme: 'mpts.theme',
  fontSize: 'mpts.fontSize',
  colorblind: 'mpts.colorblind',
  ada: 'mpts.ada',
};

function normalizeTheme(theme) {
  // Legacy values stored before the 4-theme system.
  if (theme === 'light' || !theme) return 'default';
  if (theme === 'auto') return 'default';
  return THEMES.includes(theme) ? theme : 'default';
}

function normalizeFontSize(size) {
  return FONT_SIZES.includes(size) ? size : 'medium';
}

export function loadAppearance() {
  return {
    theme: normalizeTheme(safeGet(KEYS.theme)),
    fontSize: normalizeFontSize(safeGet(KEYS.fontSize)),
    colorblind: safeGet(KEYS.colorblind) === '1',
    ada: safeGet(KEYS.ada) === '1',
  };
}

export function applyTheme(theme) {
  const t = normalizeTheme(theme);
  document.documentElement.dataset.theme = t;
  safeSet(KEYS.theme, t);
  return t;
}

export function applyFontSize(size) {
  const s = normalizeFontSize(size);
  document.documentElement.dataset.fontsize = s;
  safeSet(KEYS.fontSize, s);
  return s;
}

export function applyColorblind(on) {
  document.documentElement.classList.toggle('cb-mode', !!on);
  safeSet(KEYS.colorblind, on ? '1' : '0');
  return !!on;
}

export function applyAda(on) {
  document.documentElement.classList.toggle('ada-mode', !!on);
  safeSet(KEYS.ada, on ? '1' : '0');
  return !!on;
}

/** Apply a full preferences payload (from the API) at once. */
export function applyAppearance({ theme, font_size, colorblind_mode, ada_support } = {}) {
  if (theme !== undefined) applyTheme(theme);
  if (font_size !== undefined) applyFontSize(font_size);
  if (colorblind_mode !== undefined) applyColorblind(colorblind_mode);
  if (ada_support !== undefined) applyAda(ada_support);
}

function safeGet(key) {
  try { return localStorage.getItem(key); } catch (_e) { return null; }
}
function safeSet(key, value) {
  try { localStorage.setItem(key, value); } catch (_e) {}
}
